import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import CustomerProfile from './CustomerProfile';
import MergeCustomersModal from './MergeCustomersModal';

const API = process.env.REACT_APP_API_URL || '/api';
const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50, 100, 200, 500, 1000, 2000];

export default function CustomersTab({ t, loadData }) {
  const formatDate = (date) => { if (!date) return ''; const dateOnly = String(date).substring(0, 10); return new Date(dateOnly + 'T12:00:00').toLocaleDateString(); };

  // Data state
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Sort state
  const [sort, setSort] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Search state
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const searchTimer = useRef(null);

  // Profile modal state
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);

  // Merge state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showMerge, setShowMerge] = useState(false);

  const mg = t.admin?.merge || {};
  const pg = t.admin?.search || {};

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/customers`, {
        params: { page, limit: pageSize, q: searchDebounced, sort, order: sortOrder }
      });
      const data = res.data;
      setCustomers(data.data || []);
      if (data.pagination) {
        setTotalPages(data.pagination.totalPages || 1);
        setTotal(data.pagination.total || 0);
      }
    } catch (err) {
      console.error('Failed to load customers:', err.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchDebounced, sort, sortOrder]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearchDebounced(val);
      setPage(1); // Reset to first page on new search
    }, 300);
  };

  const handleSort = (field) => {
    if (sort === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const sortIcon = (field) => {
    if (sort !== field) return <span style={{ color: '#475569', marginLeft: '0.25rem' }}>⇅</span>;
    return <span style={{ color: '#D42B2B', marginLeft: '0.25rem' }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>;
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleMerged = () => {
    setSelectedIds(new Set());
    setShowMerge(false);
    loadCustomers();
    if (loadData) loadData();
  };

  const mergeIds = Array.from(selectedIds);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  // Pagination display
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', color: '#D42B2B', margin: 0 }}>{t.admin.allCustomers}</h2>
        {mergeIds.length === 2 && (
          <button className="btn btn-outline-danger btn-sm" onClick={() => setShowMerge(true)}>
            {mg.mergeButton || 'Merge Selected Customers'}
          </button>
        )}
      </div>

      {/* Search bar */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: '400px' }}>
          <input
            type="text"
            className="form-control"
            placeholder={pg.placeholder || 'Search by name, phone, email\u2026'}
            value={search}
            onChange={e => handleSearch(e.target.value)}
            style={{ paddingRight: '2.5rem' }}
          />
          {search && (
            <button
              onClick={() => { setSearch(''); setSearchDebounced(''); setPage(1); }}
              aria-label={t.common?.close || 'Clear'}
              style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94A3B8', fontSize: '1.1rem', cursor: 'pointer', padding: '0.25rem' }}
            >&times;</button>
          )}
        </div>
        {selectedIds.size > 0 && selectedIds.size !== 2 && (
          <span style={{ fontSize: '0.8rem', color: '#94A3B8' }}>
            {mg.selectTwo || 'Select exactly 2 customers to merge'}
          </span>
        )}
        {!loading && total > 0 && (
          <span style={{ fontSize: '0.85rem', color: '#94A3B8', marginLeft: 'auto' }}>
            {(pg.showing || '{from}\u2013{to} of {total}').replace('{from}', from).replace('{to}', to).replace('{total}', total)}
          </span>
        )}
      </div>

      {/* Table */}
      <div className="table-responsive" style={{ position: 'relative', minHeight: '200px' }}>
        {loading && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(12,18,32,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
            <span style={{ color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif" }}>{t.common?.loading || 'Loading...'}</span>
          </div>
        )}
        <table className="table table-dark table-hover">
          <thead>
          <tr>
            <th style={{ width: '2rem' }}></th>
            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>
              {t.admin.customerHeaders.name} {sortIcon('name')}
            </th>
            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('email')}>
              {t.admin.customerHeaders.email} {sortIcon('email')}
            </th>
            <th>{t.admin.customerHeaders.phone}</th>
            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('appointments')}>
              {t.admin.customerHeaders.appointments} {sortIcon('appointments')}
            </th>
            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('last_visit')}>
              {t.admin.customerHeaders.lastVisit} {sortIcon('last_visit')}
            </th>
          </tr>
          </thead>
          <tbody>
          {customers.map(customer => (
              <tr key={customer.id} onClick={() => setSelectedCustomerId(customer.id)} style={{ cursor: 'pointer' }}>
                <td onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(customer.id)}
                    onChange={e => toggleSelect(customer.id, e)}
                    style={{ accentColor: '#D42B2B' }}
                  />
                </td>
                <td style={{ color: '#E8ECF0' }}>{customer.first_name} {customer.last_name}</td>
                <td>{customer.email}</td>
                <td>
                  {customer.phone || t.admin.na}
                  {customer.phone2 && <div style={{ fontSize: '0.75rem', color: '#64748B' }}>{customer.phone2}</div>}
                </td>
                <td style={{ color: '#D42B2B' }}>{customer.appointment_count}</td>
                <td>{customer.last_appointment_date ? formatDate(customer.last_appointment_date) : t.admin.never}</td>
              </tr>
          ))}
          {!loading && customers.length === 0 && (
            <tr><td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#94A3B8' }}>{pg.noResults || 'No results found'}</td></tr>
          )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
        {totalPages > 1 && (
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            {pg.prev || '\u2039 Previous'}
          </button>
        )}
        {totalPages > 1 && (
          <span style={{ color: '#94A3B8', fontSize: '0.9rem' }}>
            Page {page} / {totalPages}
          </span>
        )}
        {totalPages > 1 && (
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            {pg.next || 'Next \u203A'}
          </button>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <label style={{ color: '#94A3B8', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{pg.rowsPerPage || 'Rows:'}</label>
          <select
            className="form-select form-select-sm"
            value={pageSize}
            onChange={e => handlePageSizeChange(Number(e.target.value))}
            style={{ width: 'auto', minWidth: '70px' }}
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Customer Profile Modal */}
      {selectedCustomerId && (
        <CustomerProfile
          customerId={selectedCustomerId}
          onClose={() => setSelectedCustomerId(null)}
          onMerged={() => { setSelectedCustomerId(null); loadCustomers(); }}
          t={t}
        />
      )}

      {/* Merge Modal */}
      {showMerge && mergeIds.length === 2 && (
        <MergeCustomersModal
          primaryId={mergeIds[0]}
          secondaryId={mergeIds[1]}
          onClose={() => setShowMerge(false)}
          onMerged={handleMerged}
          t={t}
        />
      )}
    </div>
  );
}
